import type { CaptureResult } from 'posthog-js'

import { scrubEventProperties } from './scrub-event-properties'

/**
 * PostHog `before_send` hook: scrubs every captured event with `scrubEventProperties`, except
 * the top-level `properties.token`. That one isn't the URL credential `SENSITIVE_KEYS` targets —
 * it's the project API key, which posthog-js stamps onto every event and which ingestion uses to
 * route the event to its project. Redacting it makes PostHog silently drop the event (EL-13974:
 * web tracking went to zero after release-01-09-2026). Nested `token` keys and `?token=` inside
 * `$current_url` etc. are still scrubbed.
 *
 * `$set` / `$set_once` (person properties) sit next to `properties`, not inside it, and carry
 * full hrefs too — e.g. `$set_once.$initial_current_url`, sent with `$identify` — so they're
 * scrubbed as well.
 */
export const scrubPostHogCaptureResult = (
  captureResult: CaptureResult | null,
): CaptureResult | null => {
  if (!captureResult) return captureResult

  const { $set, $set_once, properties } = captureResult

  return {
    ...captureResult,
    ...($set && { $set: scrubEventProperties($set) }),
    ...($set_once && { $set_once: scrubEventProperties($set_once) }),
    properties: { ...scrubEventProperties(properties), token: properties.token },
  }
}
